import PropTypes from 'prop-types';
import React, {useRef} from 'react';
import clsx from 'clsx';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import {makeStyles, useTheme} from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import ExpandMore from "../../../../public/images/icons/expand_more_light.svg";
import {Button} from "@material-ui/core";
import Image from "next/image";
import RSImage from "react-storefront/Image";

export const styles = theme => ({
	/**
	 * Styles applied to the root element.
	 */
	thumbs: {
		display: 'none',
		[theme.breakpoints.up('sm')]: {
			display: 'flex',
			justifyContent: 'flex-start',
			'&:not(.lightboxActive)': {
				order: -1,
				justifyContent: 'center',
				position: 'absolute',
				left: 0,
				top: 0,
				bottom: 0,
				maxWidth: 96
			},
			'&.lightboxActive': {
				justifyContent: 'center',
				marginTop: 10
			}
		}
	},
	/**
	 * Styles applied to each thumbnail element.
	 */
	thumb: {
		maxWidth: 40,
		height: 'auto',
		boxSizing: 'content-box',
		[theme.breakpoints.up('sm')]: {
			height: 120,
			maxWidth: 75
		}
	},
	/**
	 * Styles passed through to the [`Tabs`](https://material-ui.com/api/tabs/#css) element's
	 * `indicator` CSS rule.
	 */
	tabsIndicator: {
		display: 'none',
		backgroundColor: theme.palette.primary.main,
		height: '3px',
		transition: 'left 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
	},
	/**
	 * Styles applied to the root element of the Tabs element
	 */
	tabsRoot: {
		position: 'relative',
		marginTop: 15,
		marginLeft: 15,
		[theme.breakpoints.up('sm')]: {
			flexDirection: 'column',
			marginTop: 0,
			marginLeft: 0,
			marginRight: 15
		}
	},
	/**
	 * Styles applied the to the root element of the Tabs element when `thumbnailPosition` is `left` or `right`.
	 */
	tabsVertical: {
		[theme.breakpoints.up('sm')]: {
			flexDirection: 'column',
		},
	},
	/**
	 * Styles applied to the root element of the Tabs element when `thumbnailPosition` is `left`.
	 */
	tabsRootLeft: {
		[theme.breakpoints.down('xs')]: {
			marginTop: theme.spacing(2),
		},
		[theme.breakpoints.up('sm')]: {
			marginRight: theme.spacing(2),
		},
	},
	/**
	 * Styles applied to the root element of the Tabs element when `thumbnailPosition` is `right`.
	 */
	tabsRootRight: {
		[theme.breakpoints.down('xs')]: {
			marginTop: theme.spacing(2),
		},
		[theme.breakpoints.up('sm')]: {
			marginLeft: theme.spacing(2),
		},
	},
	/**
	 * Styles applied to the root element of the Tabs element when `thumbnailPosition` is `top`.
	 */
	tabsRootTop: {
		marginBottom: theme.spacing(2),
	},
	/**
	 * Styles applied to the root element of the Tabs element when `thumbnailPosition` is `bottom`.
	 */
	tabsRootBottom: {
		marginTop: theme.spacing(2),
		paddingLeft: theme.spacing(2),
		[theme.breakpoints.up('md')]: {
			paddingLeft: 0
		}
	},
	/**
	 * Styles passed through to each [`Tab`](https://material-ui.com/api/tabs/#css) element's
	 * `root` CSS rule.
	 */
	tabRoot: {
		minHeight: 80,
		minWidth: 'auto',
		padding: 0,
		lineHeight: 1,
		outline: 'none',
		[theme.breakpoints.down('sm')]: {
			minHeight: 0,
			width: 60,
			marginRight: 6,
		},
		[theme.breakpoints.up('sm')]: {
			marginBottom: 6,
			'.lightboxActive &': {
				marginBottom: 0,
				marginRight: 6
			}
		}
	},
	/**
	 * Styles passed through to each [`Tab`](https://material-ui.com/api/tabs/#css) element's
	 * `selected` CSS rule.
	 */
	selectedTab: {
		opacity: 1,
		border: '1px solid #17243e'
	},
	/**
	 * Styles passed through to each [`Tab`](https://material-ui.com/api/tabs/#css) element's
	 * `wrapper` CSS rule.
	 */
	tabWrapper: {

	},
	flexContainer: {
		padding: 1,
		[theme.breakpoints.up('sm')]: {
			flexDirection: 'column',
			'.lightboxActive &': {
				flexDirection: 'row'
			}
		}
	},
	scrollButtons: {
		display: 'none',
		left: 0,
		right: 0,
		padding: 0,
		backgroundColor: theme.palette.primary.background,
		'&:hover': {
			opacity: 0.8,
			backgroundColor: theme.palette.primary.background,
		},
		[theme.breakpoints.up('sm')]: {
			display: 'flex'
		}
	}
});

const useStyles = makeStyles(styles, {name: 'RSFCarouselThumbnails'});

/**
 * A set of thumbnails to show below a [`Carousel`](/apiReference/carousel/Carousel). Thumbnails can
 * be clicked to switch to the given slide. Internally, `CarouselThumbnails` uses MaterialUI's
 * [`Tabs`](https://material-ui.com/api/tabs) component to indicate which slide is selected
 */
function CarouselThumbnails({
		thumbnails,
		selected,
		setSelected,
		classes,
		className,
		thumbnailPosition,
		lightboxActive
	}, render) {
	const styles = useStyles({classes});
	const theme = useTheme();
	const isSmall = useMediaQuery(theme.breakpoints.down('xs'));
	const isVertical = !isSmall && ['left', 'right']?.includes(thumbnailPosition);
	const count = thumbnails.length;
	const action = useRef(null);

	function getNormalizedSelected() {
		return selected % count;
	}

	function getVideoIcon() {
		return (
			<span className="video-icon" style={{display: 'flex', flexDirection: 'column', alignItems: 'center', width: 75, padding: '10px 0'}}>
				<svg id="Layer_1" style={{enableBackground: 'new 0 0 32 32', width: 32}} viewBox="0 0 32 32"><g><g><path d="M7,28c-0.168457,0-0.3369141-0.0424805-0.4892578-0.1279297C6.1953125,27.6953125,6,27.3618164,6,27V5    c0-0.3618164,0.1953125-0.6953125,0.5107422-0.8720703c0.315918-0.1772461,0.703125-0.1699219,1.0107422,0.0185547l18,11    C25.8188477,15.3286133,26,15.6518555,26,16s-0.1811523,0.6713867-0.4785156,0.8535156l-18,11    C7.3618164,27.9511719,7.1806641,28,7,28z M8,6.7832031v18.4335938L23.0820313,16L8,6.7832031z"></path></g></g></svg>
				<span style={{textTransform: 'uppercase', marginTop: 5, fontWeight: 600, fontSize: 11}}>Video</span>
			</span>
		);
	}

	function renderTabs() {
		let tabs = thumbnails.map(({width, height, src, alt, type = 'image'}, i) => {
			let icon = '';
			if(type === 'video') {
				icon = getVideoIcon();
			} else {
				icon = <Image className={styles.thumb} width={width} height={height} src={src} alt={alt} unoptimized/>
			}



			return (
				<Tab
					classes={{
						root: styles.tabRoot,
						wrapper: styles.tabWrapper,
						selected: styles.selectedTab
					}}
					key={i}
					icon={icon}
				/>
			);
		});

		return tabs;
	}

	return (
		<div className={[clsx(className, styles.thumbs), lightboxActive ? 'lightboxActive' : ''].join(' ')}>
			<Tabs
				action={action}
				value={getNormalizedSelected()}
				variant="scrollable"
				onChange={(_, index) => setSelected(index)}
				orientation={isVertical ? 'vertical' : 'horizontal'}
				classes={{
					root: clsx(styles.tabsRoot),
					indicator: styles.tabsIndicator,
					flexContainer: styles.flexContainer,
					scrollButtons: styles.scrollButtons
				}}
				scrollButtons={"on"}
				ScrollButtonComponent={({direction, disabled, onClick}) => {
					return (!disabled ?
						<Button className={styles.scrollButtons} onClick={() => onClick()} style={{position: 'absolute', zIndex: 10, top: (direction === 'left' ? 0 : 'auto'), bottom: (direction === 'left' ? 'auto' : 0)}}>
							<ExpandMore width={48} style={{transform: direction === 'left' ? 'rotate(180deg) scale(0.8)' : 'scale(0.8)'}}/>
						</Button> : ''
					);
				}}
			>
				{renderTabs()}
			</Tabs>
		</div>
	);
}

CarouselThumbnails.propTypes = {
	/**
	 * Override or extend the styles applied to the component. See [CSS API](#css) below for more details.
	 */
	classes: PropTypes.object,

	/**
	 * CSS class to apply to the root element.
	 */
	className: PropTypes.string,

	/**
	 * Index of the currently selected slide in the [`Carousel`](/apiReference/carousel/Carousel).
	 */
	selected: PropTypes.number,

	/**
	 * Function to change the value of [`selected`](#prop-selected).
	 */
	setSelected: PropTypes.func,

	/**
	 * Array of objects containing the data for an image to be used for each thumbnail.
	 */
	thumbnails: PropTypes.arrayOf(
		PropTypes.shape({
			src: PropTypes.string,
			alt: PropTypes.string,
		}),
	),

	/**
	 * Position of the thumbnails, relative to the main carousel image.
	 */
	thumbnailPosition: PropTypes.oneOf(['bottom', 'top', 'left', 'right']),

	/**
	 * The component type to use to display images.
	 */
	ImageComponent: PropTypes.elementType,
};

CarouselThumbnails.defaultProps = {
	ImageComponent: RSImage,
};

export default CarouselThumbnails;